import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
  Fade,
  Paper,
  Box,
  Slide,
  Zoom,
  Grow,
  useMediaQuery,
  Divider,
  Typography,
} from "@mui/material";
import { forwardRef, useEffect, useState } from "react";

// Enhanced transition components with different animation styles
const FadeTransition = forwardRef(function FadeTransition(props, ref) {
  return <Fade ref={ref} {...props} timeout={{ enter: 300, exit: 200 }} />;
});

const SlideUpTransition = forwardRef(function SlideUpTransition(props, ref) {
  return (
    <Slide
      direction="up"
      ref={ref}
      {...props}
      timeout={{ enter: 300, exit: 200 }}
    />
  );
});

const ZoomTransition = forwardRef(function ZoomTransition(props, ref) {
  return <Zoom ref={ref} {...props} timeout={{ enter: 300, exit: 200 }} />;
});

const GrowTransition = forwardRef(function GrowTransition(props, ref) {
  return <Grow ref={ref} {...props} timeout={{ enter: 300, exit: 200 }} />;
});

const transitionComponents = {
  fade: FadeTransition,
  slide: SlideUpTransition,
  zoom: ZoomTransition,
  grow: GrowTransition,
};

/**
 * Enhanced DialogAtom - A modern, accessible, and highly customizable dialog component
 *
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onClose - Callback when dialog is closed
 * @param {string|React.ReactNode} props.title - Dialog title
 * @param {string} props.subtitle - Optional subtitle text
 * @param {React.ReactNode} props.children - Dialog content
 * @param {React.ReactNode} props.actions - Dialog actions (buttons)
 * @param {string} props.maxWidth - Maximum width ('xs', 'sm', 'md', 'lg', 'xl')
 * @param {boolean} props.fullWidth - Whether dialog should take full width
 * @param {boolean} props.fullScreen - Whether dialog should be fullscreen
 * @param {boolean} props.showCloseButton - Whether to show close button in title
 * @param {boolean} props.closeOnBackdropClick - Whether clicking backdrop closes dialog
 * @param {boolean} props.closeOnEscapeKey - Whether pressing Escape closes dialog
 * @param {string} props.transition - Animation type ('fade', 'slide', 'zoom', 'grow')
 * @param {string} props.variant - Visual variant ('standard', 'elevated', 'outlined', 'glass')
 * @param {Object} props.PaperProps - Additional props for Paper component
 * @param {Object} props.sx - Additional styling
 * @param {boolean} props.loading - Shows loading state
 * @param {string} props.size - Predefined size ('small', 'medium', 'large')
 */
export function DialogAtom({
  open = false,
  onClose,
  title,
  subtitle,
  children,
  actions,
  dialogTitle,
  maxWidth = "sm",
  fullWidth = true,
  fullScreen = false,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscapeKey = true,
  transition = "fade",
  variant = "glass",
  PaperProps = {},
  sx = {},
  loading = false,
  size = "medium",
  ...props
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isClosing, setIsClosing] = useState(false);

  // Enhanced close handler with animation state
  const handleClose = (event, reason) => {
    if (!closeOnBackdropClick && reason === "backdropClick") return;
    if (!closeOnEscapeKey && reason === "escapeKeyDown") return;

    if (onClose) {
      setIsClosing(true);
      // Small delay to show closing animation
      setTimeout(() => {
        setIsClosing(false);
        onClose(event, reason);
      }, 50);
    }
  };

  // Keyboard navigation enhancement
  useEffect(() => {
    if (open && closeOnEscapeKey) {
      const handleKeyDown = (event) => {
        if (event.key === "Escape") {
          handleClose(event, "escapeKeyDown");
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, closeOnEscapeKey]);

  // Size configurations
  const sizeConfigs = {
    small: { maxWidth: "xs", padding: { title: 2, content: 2, actions: 1.5 } },
    medium: { maxWidth: "md", padding: { title: 2, content: 2, actions: 2 } },
    large: { maxWidth: "lg", padding: { title: 2, content: 2, actions: 2.5 } },
    extralarge: {
      maxWidth: "xl",
      padding: { title: 2, content: 2, actions: 2.5 },
    },
  };

  const currentSize = sizeConfigs[size] || sizeConfigs.medium;
  const effectiveMaxWidth = currentSize.maxWidth || maxWidth;

  // Variant styles
  const variantStyles = {
    standard: {
      backgroundColor: theme.palette.background.paper,
      backgroundImage: "none",
      boxShadow: theme.shadows[8],
    },
    elevated: {
      backgroundColor: theme.palette.background.paper,
      backgroundImage: `linear-gradient(${
        theme.palette.mode === "dark"
          ? "0deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%"
          : "0deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 100%"
      })`,
      boxShadow: `0 8px 32px ${
        theme.palette.mode === "dark"
          ? "rgba(0, 0, 0, 0.5)"
          : "rgba(0, 0, 0, 0.12)"
      }`,
      border: `1px solid ${theme.palette.divider}`,
    },
    outlined: {
      backgroundColor: theme.palette.background.paper,
      backgroundImage: "none",
      border: `2px solid ${theme.palette.primary.main}`,
      boxShadow: theme.shadows[4],
    },
    glass: {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(30, 30, 30, 0.8)"
          : "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(20px)",
      border: `1px solid ${
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.1)"
      }`,
      boxShadow: `0 8px 32px ${
        theme.palette.mode === "dark"
          ? "rgba(0, 0, 0, 0.3)"
          : "rgba(0, 0, 0, 0.1)"
      }`,
    },
  };

  const TransitionComponent =
    transitionComponents[transition] || FadeTransition;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={effectiveMaxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen || (isMobile && size === "large")}
      TransitionComponent={TransitionComponent}
      PaperComponent={Paper}
      disableEscapeKeyDown={!closeOnEscapeKey}
      PaperProps={{
        elevation: variant === "glass" ? 0 : 8,
        sx: {
          borderRadius: fullScreen ? 0 : theme.spacing(2),
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: isClosing ? "scale(0.95)" : "scale(1)",
          opacity: isClosing ? 0.8 : 1,
          overflowY: "auto",
          ...variantStyles[variant],
          ...PaperProps.sx,
        },
        ...PaperProps,
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor:
            variant === "glass" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.5)",
          backdropFilter: variant === "glass" ? "blur(8px)" : "blur(4px)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        "& .MuiDialog-paper": {
          margin: fullScreen ? 0 : theme.spacing(isMobile ? 1 : 2),
          maxHeight: fullScreen ? "100vh" : "calc(100vh - 64px)",
        },
        ...sx,
      }}
      {...props}
    >
      {dialogTitle && <dialogTitle />}
      {/* Enhanced Dialog Title */}
      {(title || subtitle) && (
        <DialogTitle
          sx={{
            pb: subtitle ? 1 : currentSize.padding.title,
            pt: currentSize.padding.title,
            pr: showCloseButton ? 7 : currentSize.padding.title,
            pl: currentSize.padding.title,
            position: "relative",
            borderBottom:
              variant !== "glass"
                ? `1px solid ${theme.palette.divider}`
                : "none",
            backgroundColor: "transparent",
            minHeight: showCloseButton ? 56 : "auto",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                fontSize:
                  size === "small"
                    ? "1.1rem"
                    : size === "large"
                      ? "1.4rem"
                      : "1.25rem",
                lineHeight: 1.2,
                color: theme.palette.text.primary,
                wordBreak: "break-word",
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: theme.palette.text.secondary,
                  fontSize: "0.875rem",
                  lineHeight: 1.4,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {showCloseButton && onClose && (
            <IconButton
              aria-label="close dialog"
              onClick={(e) => handleClose(e, "closeButton")}
              size={size === "small" ? "small" : "medium"}
              sx={{
                position: "absolute",
                right: theme.spacing(1),
                top: "50%",
                transform: "translateY(-50%)",
                color: theme.palette.text.secondary,
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.text.primary,
                  transform: "translateY(-50%) scale(1.1)",
                },
                "&:active": {
                  transform: "translateY(-50%) scale(0.95)",
                },
              }}
            >
              <CloseIcon fontSize={size === "small" ? "small" : "medium"} />
            </IconButton>
          )}
        </DialogTitle>
      )}

      {/* Enhanced Dialog Content */}
      {children && (
        <>
          {children}

          {/* Loading overlay */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                zIndex: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Enhanced Dialog Actions */}
      {actions && (
        <>
          {variant !== "glass" && <Divider />}
          <DialogActions
            sx={{
              px: currentSize.padding.content,
              py: currentSize.padding.actions,
              backgroundColor: "transparent",
              gap: 1.5,
              justifyContent: "flex-end",
              flexWrap: isMobile ? "wrap" : "nowrap",
              "& > *": {
                minWidth: isMobile ? "100%" : "auto",
                order: isMobile ? 1 : "unset",
              },
              "& > *:first-of-type": {
                order: isMobile ? 2 : "unset",
              },
            }}
          >
            {actions}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

// Export default for convenience
export default DialogAtom;
